// THE /services LISTS, DERIVED FROM WHAT THE SERVER PUBLISHES.
//
// The page used to hold its own array of 21 add-on names and its own array of
// six wash names. Nine services were retired in the database in the same week
// those arrays were written; the page went on advertising them, useLivePrice
// found no price for them (it has no baked fallback, by design) and they
// rendered as permanent blank placeholders in front of every visitor.
//
// Membership is now the server's answer and nothing here decides it. These
// functions filter and order what they are handed, and refuse anything they
// cannot price. The server side of the same table is app/services/
// customer_catalogue.rb, which also publishes `section` and `page_rank`, so
// the curation (outside/inside, and what people actually buy, highest first)
// is preserved without any of it being reimplemented here.
//
// PURE ON PURPOSE: no React, no store, no stylesheet, no lodash. It is the
// page's only logic worth asserting on, and `node --test client/gwmc/lib/
// service-catalogue.test.mjs` runs it directly, with no bundler and no
// browser.

// Where an add-on the server did not classify is shown. It has to be
// somewhere: the failure this file exists to stop is a service that silently
// renders nowhere.
export const DEFAULT_SECTION = 'outside'

// The two sections, in page order, with the headings the customer reads. The
// headings say "the car" on purpose: "Outside" and "Inside" alone read as
// filter buttons, while "Outside the car" and "Inside the car" are the words
// somebody would say out loud.
export const SECTIONS = [
  { id: 'outside', title: 'Outside the car' },
  { id: 'inside', title: 'Inside the car' }
]

const SECTION_IDS = SECTIONS.map(section => section.id)

const list = value => (Array.isArray(value) ? value : [])

// A row we cannot price is not a row. This is the whole of the blank
// placeholder bug in one line: the old page rendered the name and then an
// empty box where the price should be, for ever.
//
// TWO FIELDS, AND ONLY A STRING COUNTS. The gwmc:catalogue meta tag calls the
// formatted amount `price`; the API calls it `customerPrice` and ALSO has a
// `price`, which store/adapters/services.js fills with a currency.js object
// for the booking screen's arithmetic. Taking `price` first looked tidy and
// silently priced every API row at null, because an object is not a string.
// So: the first of the two that is actually a non-empty string.
const priceOf = entry => {
  const price = [entry.customerPrice, entry.price].find(value => typeof value === 'string' && value.trim() !== '')

  if (!price) return null
  // The API also sends the amount in pence. When it is there it has to agree
  // that this is something we charge for; a half-entered service in the
  // services table is £0 and must not reach the page.
  if (entry.customerPriceCents !== undefined && !(Number(entry.customerPriceCents) > 0)) return null

  return price
}

const rankOf = entry => (typeof entry.pageRank === 'number' ? entry.pageRank : Number.MAX_SAFE_INTEGER)

const byRank = (a, b) => a.rank - b.rank

// THE SIX WASHES, from either of the two things that publish them: the
// gwmc:catalogue meta tag (`{slug, key, name, price}`, already in page order) or
// /api/v2/services (the same four under its own wire names, plus `group` and
// `pageRank`). One shape comes out, so the page has one rendering.
//
// `descriptors` is the page's own curated copy, keyed by booking slug. A wash
// the copy does not know about keeps its database description rather than
// losing its line, and a wash with neither renders with no descriptor rather
// than not rendering.
export const washesFrom = (entries, { descriptors = {} } = {}) =>
  list(entries)
    .filter(entry => entry && (entry.group === undefined || entry.group === 'base'))
    .map(entry => ({ entry, price: priceOf(entry), rank: rankOf(entry) }))
    .filter(({ entry, price }) => entry.slug && entry.name && price)
    .sort(byRank)
    .map(({ entry, price }) => ({
      slug: entry.slug,
      key: entry.key,
      name: entry.name,
      price,
      descriptor: descriptors[entry.slug] || entry.description || '',
      // The wash's own page, from gwmc:catalogue; the API rows have none.
      ...(entry.path ? { path: entry.path } : {})
    }))

// THE ADD-ONS, grouped into the two sections and ordered inside each one by
// the server's `page_rank`, which is the last 24 months of paid orders,
// highest first.
//
// An add-on whose section the server does not recognise falls into
// DEFAULT_SECTION rather than off the page. An add-on the server marks
// `on_prices_page: false` is held back on purpose (see PAGE_SUPPRESSED in
// customer_catalogue.rb). An empty section is not rendered at all, so a page
// with nothing inside the car is one card rather than one card and a heading
// over nothing.
//
// `labels` is the page's own copy, keyed by the catalogue key rather than by
// the database name, because the database names were typed over nine years by
// different people and a tidy-up there must not be able to blank a row here. A
// key the copy does not know about reads its database name, which is correct
// English in every case and merely less tidy than the hand-written version.
export const addOnSectionsFrom = (entries, { labels = {} } = {}) => {
  const rows = list(entries)
    .filter(entry => entry && entry.section && entry.onPricesPage !== false && entry.name)
    .map(entry => ({ entry, price: priceOf(entry), rank: rankOf(entry) }))
    .filter(({ price }) => price)

  return SECTIONS.map(section => ({
    id: section.id,
    title: section.title,
    items: rows
      .filter(({ entry }) => {
        const id = SECTION_IDS.includes(entry.section) ? entry.section : DEFAULT_SECTION
        return id === section.id
      })
      .sort(byRank)
      .map(({ entry, price }) => ({
        key: entry.key || entry.name,
        name: entry.name,
        label: labels[entry.key] || entry.name,
        price
      }))
  })).filter(section => section.items.length > 0)
}
